import { EntityCommonInfoModel, FilteredPagedQueryResult, FilteredPagedModel, CommonMultiDeleteModel } from "../../entities/common";
import { MonthlyPerformanceModel, MonthlyPerformanceValidationResponse } from "../../entities/monthlyPerformance";
import { apiSlice, TagTypes } from "./apiSlice";
import { getQuerySearchParams } from "../../utils";

export const monthlyPerformanceApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getMonthlyPerformanceCommonInfo: builder.query<EntityCommonInfoModel, boolean>({
      query: (isCumulative: boolean) => `/monthlyPerformance/${isCumulative}/commonInfo`,
      providesTags: [{ type: TagTypes.MonthlyPerformance, id: "LIST" }],
    }),
    getMonthlyPerformanceFilteredPaged: builder.query<FilteredPagedQueryResult<MonthlyPerformanceModel>, FilteredPagedModel>({
      query: ({ isCumulative, page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/monthlyPerformance/${isCumulative}/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagTypes.MonthlyPerformance, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }: { id: number | string }) => ({
                type: TagTypes.MonthlyPerformance,
                id,
              })),
            ]
          : [{ type: TagTypes.MonthlyPerformance, id: "PARTIAL-LIST" }],
    }),
    getMonthlyPerformance: builder.query<MonthlyPerformanceModel, number>({
      query: (monthlyPerformanceId) => `/monthlyPerformance/${monthlyPerformanceId}`,
      providesTags: (result, error, arg) => [{ type: TagTypes.MonthlyPerformance, id: arg }],
    }),
    validateMonthlyPerformance: builder.mutation<MonthlyPerformanceValidationResponse, MonthlyPerformanceModel>({
      query: (monthlyPerformance) => ({
        url: "/monthlyPerformance/validate",
        method: "POST",
        body: monthlyPerformance,
      }),
    }),
    scrapMonthlyPerformance: builder.query<MonthlyPerformanceModel, { isCumulative: boolean; tracingNumber: string }>({
      query: ({ isCumulative, tracingNumber }) => ({
        url: `/monthlyPerformance/scrap/${isCumulative}`,
        method: "POST",
        body: { tracingNumber },
      }),
      providesTags: [{ type: TagTypes.MonthlyPerformance, id: "Scrap" }],
    }),
    addMonthlyPerformance: builder.mutation({
      query: (initialMonthlyPerformance) => ({
        url: "/monthlyPerformance",
        method: "POST",
        body: initialMonthlyPerformance,
      }),
      invalidatesTags: [
        { type: TagTypes.MonthlyPerformance, id: "LIST" },
        { type: TagTypes.MonthlyPerformance, id: "PARTIAL-LIST" },
      ],
    }),
    editMonthlyPerformance: builder.mutation({
      query: (monthlyPerformance) => ({
        url: `/monthlyPerformance/${monthlyPerformance.id}`,
        method: "PUT",
        body: monthlyPerformance,
      }),
      invalidatesTags: (result, error, arg) => [{ type: TagTypes.MonthlyPerformance, id: arg.id }],
    }),
    deleteMonthlyPerformance: builder.mutation({
      query: (monthlyPerformanceId) => ({
        url: `/monthlyPerformance/${monthlyPerformanceId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagTypes.MonthlyPerformance, id: arg },
              { type: TagTypes.MonthlyPerformance, id: "LIST" },
            ],
    }),
    deleteMultiMonthlyPerformances: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/monthlyPerformance/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id: number) => ({
                type: TagTypes.MonthlyPerformance,
                id,
              })),
              { type: TagTypes.MonthlyPerformance, id: "LIST" },
            ],
    }),
  }),
});

export const {
  useGetMonthlyPerformanceCommonInfoQuery,
  useGetMonthlyPerformanceFilteredPagedQuery,
  useGetMonthlyPerformanceQuery,
  useValidateMonthlyPerformanceMutation,
  useScrapMonthlyPerformanceQuery,
  useLazyScrapMonthlyPerformanceQuery,
  useAddMonthlyPerformanceMutation,
  useEditMonthlyPerformanceMutation,
  useDeleteMonthlyPerformanceMutation,
  useDeleteMultiMonthlyPerformancesMutation,
} = monthlyPerformanceApi;
